import type { FormVariant } from "@shared/types";
import { REQUIRED_GRIND } from "../constants";

/**
 * Maps values from whole bean variants to other grinds of the same bag size.
 *
 * Logic copied from client formValidation.tsx lines 143-164.
 *
 * This is a PRE-SUBMISSION data transformation that copies pricing and inventory
 * fields from whole bean variants to all other grinds with the same bag size.
 *
 * Fields copied:
 * - price
 * - wholesalePrice
 * - term
 * - startDate
 * - endDate
 * - weeklyInventory
 * - totalMaxShortTermInventory
 * - ship_to (cross-border ship-to is bag-size based)
 *
 * @param variants - Array of form variants to process
 * @returns The same array with values copied from whole bean to other grinds
 */
export function mapWholeBeanVariants(variants: FormVariant[]): FormVariant[] {
  // Filter variants that have the required grind type (whole bean)
  const wholeBeanVariants = variants.filter((x) => x.grind?.includes(REQUIRED_GRIND));

  return variants.map((variant) => {
    // Whole bean variants are not modified
    if (variant.grind?.includes(REQUIRED_GRIND)) {
      return variant;
    }

    const variantBagSize = variant.mapDetectedBagSizeTo ?? variant.bagSize;

    // Find the whole bean variant with the same bag size
    const matchingWholeBean = wholeBeanVariants.find((wb) => {
      const wbBagSize = wb.mapDetectedBagSizeTo ?? wb.bagSize;
      return wbBagSize === variantBagSize;
    });

    if (!matchingWholeBean) {
      return variant;
    }

    return {
      ...variant,
      price: matchingWholeBean.price,
      wholesalePrice: matchingWholeBean.wholesalePrice,
      term: matchingWholeBean.term,
      startDate: variant.startDate ?? matchingWholeBean.startDate,
      endDate: variant.endDate ?? matchingWholeBean.endDate,
      weeklyInventory: matchingWholeBean.weeklyInventory,
      totalMaxShortTermInventory: matchingWholeBean.totalMaxShortTermInventory,
      // Inherit the whole-bean's ship-to, but never erase a grind's own value
      // when the whole-bean row has none (undefined) — e.g. legacy/unseeded data.
      ship_to: matchingWholeBean.ship_to ?? variant.ship_to,
    };
  });
}

/**
 * Derives the product-level `ship_to` from the variants after ship-to became
 * bag-size based.
 *
 * The Shopify-import submission mapper (`client-shared`
 * `salesforceProductTransformations`) still reads a single product-level
 * `ship_to` (spread across every per-bag-size Salesforce row). To keep that
 * path unchanged while ship-to lives on the variant, we re-populate
 * `FormProduct.ship_to` from the whole-bean variant that owns the value — the
 * same variant every other grind of its bag size inherits from. The first
 * whole-bean variant carrying a non-empty `ship_to` wins; any variant with a
 * value is the fallback. Returns `undefined` when no variant has a ship-to.
 *
 * Only variants selected for import are considered: the caller passes the full
 * variants array (selected + unselected), and an unselected whole-bean row
 * auto-seeded with the product/home ship-to must never override a selected
 * row's destination (per review feedback). Falls back to all variants only when
 * none are selected.
 */
export function deriveProductShipTo(variants: FormVariant[]): string | undefined {
  const selected = variants.filter((variant) => variant.isProductSelectedForImport);
  const pool = selected.length > 0 ? selected : variants;
  const wholeBeanWithShipTo = pool.find((variant) => variant.grind?.includes(REQUIRED_GRIND) && !!variant.ship_to);
  const chosen = wholeBeanWithShipTo ?? pool.find((variant) => !!variant.ship_to);
  return chosen?.ship_to ?? undefined;
}
